import React from 'react';
import ReactDOM from 'react-dom';

type Position = {
  top: number;
  left: number;
}

const getRoot = (id: string) => {
  let $root = document.getElementById(id);
  if (!$root) {
    $root = document.createElement('div');
    $root.id = id;
    $root.style.display = 'none';
    $root.style.position = 'fixed';
    $root.style.top = '0';
    $root.style.left = '0';
    $root.style.zIndex = '100';
    document.body.appendChild($root);
  }
  return $root as HTMLDivElement;
};

type OpenArguments<T> = { position?: Position } & T;
type CreateModalOption = {
  autoHide?: boolean;
  zIndex?: number;
}

const defaultOption: CreateModalOption = {
  autoHide: false,
  zIndex: 100,
};

function createModal<T>(id: string, Component: React.FC<T>, option?: Partial<CreateModalOption>) {
  const {
    autoHide,
    zIndex,
  } = { ...defaultOption, ...option };

  const autoHideEvent = (e: MouseEvent) => {
    const target = (e.target as HTMLElement).closest(`#${id}`);
    const $root = getRoot(id);
    if (target !== $root) {
      $root.style.display = 'none';
      document.removeEventListener('click', autoHideEvent);
      ReactDOM.unmountComponentAtNode($root);
    }
  };

  const hide = () => {
    const $root = getRoot(id);
    $root.style.display = 'none';
    document.removeEventListener('click', autoHideEvent);
    ReactDOM.unmountComponentAtNode($root);
  };

  const open = ({ position, ...props }: OpenArguments<T>) => {
    const $root = getRoot(id);
    $root.style.zIndex = `${zIndex}`;

    if ($root.style.display === 'none') {
      $root.style.display = 'block';
      ReactDOM.render(
        React.createElement(Component as any, props as any),
        $root,
      );
      setTimeout(() => {
        if (autoHide) {
          document.addEventListener('click', autoHideEvent);
        }
      }, 0);
    }

    if (!position) return;

    let { left, top } = position;
    const [screenWidth, screenHeight] = [window.innerWidth, window.innerHeight];
    const [width, height] = [$root.clientWidth, $root.clientHeight];
    if (left + width > screenWidth) {
      left -= width;
      left = Math.max(left, 0);
    }
    if (top + height > screenHeight) {
      top -= height;
      top = Math.max(top, 0);
    }

    $root.style.left = `${left}px`;
    $root.style.top = `${top}px`;
  };

  return { open, hide };
}

export default createModal;
